import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { orderedImpacts, Valence } from '@upright/common/model/types';
import { Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';

export const defaultLensStateName = 'default' as const;

export interface LeftAndRightImpactSelectionState {
  left: ImpactCategoryId | undefined;
  right: ImpactCategoryId | undefined;
  lastSide?: Side; // keeping track of this is useful for mobile
}

function serializeSelectionState(state: LeftAndRightImpactSelectionState) {
  return `${state.left || defaultLensStateName};${state.right || defaultLensStateName};${String(state.lastSide)}`;
}

function unserializeSelectionState(value: string | undefined): LeftAndRightImpactSelectionState {
  if (!value) {
    return { left: undefined, right: undefined };
  }
  const [left, right, lastSide] = value.split(';');

  function parseSplitValue(val: string | undefined) {
    if (!val) {
      return undefined;
    }
    if (orderedImpacts.indexOf(val as ImpactCategoryId) === -1) {
      return undefined;
    }
    return val as ImpactCategoryId;
  }

  function parseLastSide(val: string | undefined) {
    if (!val) {
      return undefined;
    }
    const parsed: unknown = parseInt(val);
    if (parsed === Side.LEFT) {
      return Side.LEFT;
    } else if (parsed === Side.RIGHT) {
      return Side.RIGHT;
    }
    return undefined;
  }

  return {
    left: parseSplitValue(left),
    right: parseSplitValue(right),
    lastSide: parseLastSide(lastSide),
  };
}

function onImpactDataAreaClick(
  currentSelection: LeftAndRightImpactSelectionState,
  impact: ImpactCategoryId,
  valence: Valence,
) {
  if (valence === Valence.NEGATIVE) {
    return {
      ...currentSelection,
      left: currentSelection.left === impact ? undefined : impact,
      lastSide: Side.LEFT,
    };
  } else if (valence === Valence.POSITIVE) {
    return {
      ...currentSelection,
      right: currentSelection.right === impact ? undefined : impact,
      lastSide: Side.RIGHT,
    };
  }
  return currentSelection;
}

function isImpactDataAreaHighlighted(
  state: LeftAndRightImpactSelectionState,
  impactId: ImpactCategoryId,
  valence: Valence,
) {
  switch (valence) {
    case Valence.NEGATIVE:
      return state.left === impactId;
    case Valence.POSITIVE:
      return state.right === impactId;
    case Valence.OTHER:
    default:
      throw Error(`Unknown valence ${valence} for impact bar.`);
  }
}

function isImpactHighlighted(_selectionStatus: LeftAndRightImpactSelectionState, _impactId: ImpactCategoryId) {
  return false;
}

function showClearSelectionButton(selectionStatus: LeftAndRightImpactSelectionState) {
  return selectionStatus.left !== undefined || selectionStatus.right !== undefined;
}

function getClearedSelectionState(_selectionState: LeftAndRightImpactSelectionState) {
  return { left: undefined, right: undefined, lastSide: undefined };
}

export const separateLeftRightSelectionState = {
  // TODO should this be removed?
  onImpactDataAreaClick,
  isImpactDataAreaHighlighted,
  isImpactHighlighted,
  serializeSelectionState,
  unserializeSelectionState,
  showClearSelectionButton,
  getClearedSelectionState,
};
